import type { Child } from '../types/child';
import type { Subject } from '../types/activity';
import type { EnrichmentSlot } from '../types/schedule';
import {
  getParentCurriculumSettings,
  type ParentCurriculumQueueItem,
  useParentCurriculumStore,
} from '../store/parentCurriculumStore';
import { useCourseStore } from '../store/courseStore';

const SUBJECT_MAP: Record<string, Subject> = {
  arts: 'creative',
  business: 'financial',
  engineering: 'engineering',
  history: 'social-studies',
  literacy: 'literacy',
  math: 'math',
  music: 'music',
  nature: 'science',
  science: 'science',
  space: 'science',
  words: 'literacy',
  quizzes: 'eq',
  arcade: 'physical',
  trades: 'engineering',
  creative: 'creative',
  wellness: 'body-wellness',
  'visual-arts': 'visual-arts',
};

export function isDirectedSequenceMode(childId: string): boolean {
  const settings = getParentCurriculumSettingsForChild(childId);
  return settings.mode === 'sequenced' || settings.lockNavigationToQueue;
}

export function isParentCurriculumSlot(slot: EnrichmentSlot): boolean {
  return slot.id.startsWith('parent-curriculum-');
}

export function getParentCurriculumItemId(slotId: string): string {
  return slotId.replace(/^parent-curriculum-/, '');
}

export function getParentCurriculumItemsForToday(child: Child, date: string): ParentCurriculumQueueItem[] {
  const store = useParentCurriculumStore.getState();
  const courseStore = useCourseStore.getState();
  return store
    .getItemsForToday(child.id, date)
    .filter((item) =>
      item.itemType !== 'course-lesson'
      || !courseStore.isLessonCompleted(child.id, item.courseId, item.lessonId, item.ageGroup),
    );
}

export function getParentCurriculumSettingsForChild(childId: string) {
  return getParentCurriculumSettings(useParentCurriculumStore.getState(), childId);
}

export function buildParentCurriculumSlots(child: Child, date: string): EnrichmentSlot[] {
  return getParentCurriculumItemsForToday(child, date).map((item, idx) => ({
    id: `parent-curriculum-${item.id}`,
    type: item.itemType === 'game' ? 'game' : item.itemType === 'activity' ? 'activity' : 'course',
    title: item.title,
    emoji: item.courseEmoji || '📖',
    durationMinutes: item.durationMinutes,
    status: 'pending',
    placement: 'bonus',
    linkTo: item.route,
    linkParams: item.routeParams,
    reason: item.required
      ? `Parent selected from ${item.courseTitle}`
      : `Optional parent pick from ${item.courseTitle}`,
    subject: SUBJECT_MAP[item.subject] ?? SUBJECT_MAP[item.category],
    xpReward: 20,
    coinReward: 8,
    completedAt: null,
    afterBlockIndex: idx,
  }));
}

export function mergeParentCurriculumSlots(
  childId: string,
  parentSlots: EnrichmentSlot[],
  generatedSlots: EnrichmentSlot[],
): EnrichmentSlot[] {
  const settings = getParentCurriculumSettingsForChild(childId);
  if (settings.mode === 'sequenced' || settings.lockNavigationToQueue) return parentSlots;
  if (settings.mode === 'locked') return parentSlots;
  if (settings.mode === 'guided') return parentSlots;
  if (settings.requireQueueBeforeExtras && parentSlots.length > 0) return parentSlots;
  if (!settings.allowRecommendations) return parentSlots;
  return [...parentSlots, ...generatedSlots];
}

export function getQueueItemUrl(item: ParentCurriculumQueueItem): string {
  const params = new URLSearchParams(item.routeParams ?? {});
  return params.toString() ? `${item.route}?${params.toString()}` : item.route;
}

export function isQueueItemRoute(item: ParentCurriculumQueueItem, pathname: string): boolean {
  if (item.itemType === 'course-lesson') return pathname === '/courses';
  if (item.itemType === 'game') return pathname === '/games';
  if (item.itemType === 'activity') return pathname === `/activity/${item.itemId}`;
  return pathname === '/';
}

export function getCurrentDirectedSequenceItem(child: Child, date: string): ParentCurriculumQueueItem | null {
  return useParentCurriculumStore.getState().getCurrentSequenceItem(child.id, date);
}
